import { useState } from 'react';
import {
  CheckCircle,
  XCircle,
  CircleDashed,
  ChevronDown,
  ChevronUp,
  Ban,
} from 'lucide-react';
import type {
  FallbackTrace,
  FallbackTraceAttempt,
  FallbackTraceChannel,
  FallbackTraceTiming,
} from './types';

interface FallbackTraceTimelineProps {
  trace: FallbackTrace;
}

type NodeStatus = 'success' | 'failed' | 'skipped';

interface TimelineNode {
  channel: FallbackTraceChannel;
  status: NodeStatus;
  attempt?: FallbackTraceAttempt;
  timing?: FallbackTraceTiming;
  /** 该节点之后是否触发了短路（不再尝试后续渠道） */
  shortCircuitAfter?: boolean;
}

function formatDuration(ms?: number): string {
  if (typeof ms !== 'number' || !Number.isFinite(ms)) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(ms < 10000 ? 2 : 1)}s`;
}

/** 把 attemptChain + attempts + successChannel 合并成有序的节点列表 */
function buildTimelineNodes(trace: FallbackTrace): { nodes: TimelineNode[]; shortCircuitIndex: number } {
  const successId = trace.successChannel?.channelId;
  const failuresById = new Map<string, FallbackTraceAttempt>();
  for (const attempt of trace.attempts || []) {
    if (attempt?.channelId) failuresById.set(attempt.channelId, attempt);
  }

  let shortCircuitIndex = -1;
  const nodes: TimelineNode[] = (trace.attemptChain || []).map((channel, index) => {
    const failed = failuresById.get(channel.channelId);
    if (failed) {
      return {
        channel,
        status: 'failed' as const,
        attempt: failed,
        timing: { startedAt: failed.startedAt, endedAt: failed.endedAt, durationMs: failed.durationMs },
      };
    }
    if (channel.channelId === successId) {
      return {
        channel,
        status: 'success' as const,
        timing: {
          startedAt: trace.successChannel?.startedAt ?? channel.startedAt,
          endedAt: trace.successChannel?.endedAt ?? channel.endedAt,
          durationMs: trace.successChannel?.durationMs ?? channel.durationMs,
        },
      };
    }
    // 没有失败记录也不是成功渠道：被短路跳过
    if (shortCircuitIndex === -1) shortCircuitIndex = index;
    return { channel, status: 'skipped' as const };
  });

  // 短路节点：被跳过的第一个渠道之前那个失败节点触发
  return { nodes, shortCircuitIndex };
}

function NodeIcon({ status }: { status: NodeStatus }) {
  if (status === 'success') return <CheckCircle className="w-5 h-5 text-green-600 shrink-0" />;
  if (status === 'failed') return <XCircle className="w-5 h-5 text-red-500 shrink-0" />;
  return <CircleDashed className="w-5 h-5 text-gray-300 shrink-0" />;
}

function ProviderResponseBlock({ attempt }: { attempt: FallbackTraceAttempt }) {
  const [open, setOpen] = useState(false);
  if (attempt.detail && !attempt.providerResponse) {
    return (
      <div className="mt-2">
        <button
          onClick={() => setOpen(!open)}
          className="text-xs text-gray-500 flex items-center gap-1 hover:text-gray-700"
        >
          {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          查看错误堆栈
        </button>
        {open && (
          <pre className="mt-1.5 text-xs text-red-700 whitespace-pre-wrap break-words max-h-40 overflow-y-auto bg-red-50 rounded-lg p-2 border border-red-100">
            {attempt.detail}
          </pre>
        )}
      </div>
    );
  }
  if (!attempt.providerResponse) return null;
  let body: string;
  try {
    body = typeof attempt.providerResponse === 'string'
      ? JSON.stringify(JSON.parse(attempt.providerResponse), null, 2)
      : JSON.stringify(attempt.providerResponse, null, 2);
  } catch {
    body = String(attempt.providerResponse);
  }
  return (
    <div className="mt-2">
      <button
        onClick={() => setOpen(!open)}
        className="text-xs text-gray-500 flex items-center gap-1 hover:text-gray-700"
      >
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        查看上游响应
      </button>
      {open && (
        <pre className="mt-1.5 text-xs text-gray-700 whitespace-pre-wrap break-words max-h-40 overflow-y-auto bg-white rounded-lg p-2 border border-gray-200">
          {body}
        </pre>
      )}
    </div>
  );
}

export default function FallbackTraceTimeline({ trace }: FallbackTraceTimelineProps) {
  const { nodes, shortCircuitIndex } = buildTimelineNodes(trace);
  const failedCount = trace.attempts?.length || 0;
  const totalCount = nodes.length;
  const skippedCount = nodes.filter((n) => n.status === 'skipped').length;

  let summaryText: string;
  let summaryTone: string;
  if (trace.shortCircuitRule) {
    summaryText = `降级已短路 · 尝试 ${failedCount} 个渠道后命中终止规则`;
    summaryTone = 'text-amber-800 bg-amber-50 border-amber-200';
  } else if (trace.recovered) {
    summaryText = `已降级恢复 · 共尝试 ${totalCount} 个渠道，其中 ${failedCount} 个失败`;
    summaryTone = 'text-green-800 bg-green-50 border-green-200';
  } else {
    summaryText = `降级失败 · 共尝试 ${totalCount} 个渠道均未成功`;
    summaryTone = 'text-red-800 bg-red-50 border-red-200';
  }

  return (
    <div className="space-y-3">
      {/* 顶部摘要 */}
      <div className={`text-xs sm:text-sm font-medium px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-lg border ${summaryTone}`}>
        {summaryText}
        {trace.enableFallbackChain === false && (
          <span className="ml-2 text-xs opacity-70">（未启用降级链）</span>
        )}
      </div>

      {/* 时间线 */}
      <div className="relative pl-1">
        {nodes.map((node, index) => {
          const isLast = index === nodes.length - 1;
          return (
            <div key={`${node.channel.channelId}-${index}`} className="relative">
              {/* 竖线（除最后一个节点外） */}
              {!isLast && (
                <div className="absolute left-[9px] top-5 bottom-0 w-px bg-gray-200" />
              )}
              <div className="flex items-start gap-3 pb-3 sm:pb-4 last:pb-0">
                <div className="pt-0.5">
                  <NodeIcon status={node.status} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:justify-between sm:gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm font-medium text-gray-900 truncate">
                        {node.channel.channelName || node.channel.channelId}
                      </span>
                      {node.channel.provider && (
                        <span className="hidden sm:inline text-xs text-gray-400 truncate">{node.channel.provider}</span>
                      )}
                      {node.status === 'success' && (
                        <span className="text-xs text-green-700 bg-green-100 px-1.5 py-0.5 rounded shrink-0">最终接管</span>
                      )}
                      {node.status === 'skipped' && (
                        <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded shrink-0">未尝试</span>
                      )}
                    </div>
                    <span className="text-xs text-gray-400 shrink-0">
                      耗时 {formatDuration(node.timing?.durationMs)}
                    </span>
                  </div>

                  {/* 失败详情 */}
                  {node.status === 'failed' && node.attempt && (
                    <div className="mt-1 text-xs text-red-600">
                      {node.attempt.status ? (
                        <span>
                          HTTP {node.attempt.status}
                          {node.attempt.statusText ? ` · ${node.attempt.statusText}` : ''}
                        </span>
                      ) : (
                        <span>上游异常</span>
                      )}
                      <ProviderResponseBlock attempt={node.attempt} />
                    </div>
                  )}

                  {/* 短路提示：挂在该失败节点之后 */}
                  {index === shortCircuitIndex - 1 && trace.shortCircuitRule && (
                    <div className="mt-2 flex items-start gap-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
                      <Ban className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                      <span>
                        内容审核类确定性失败，不再尝试后续 {skippedCount} 个渠道。
                        规则：<code className="font-mono">{trace.shortCircuitRule.id}</code>
                        <span className="block text-amber-600 mt-0.5">{trace.shortCircuitRule.userMessage}</span>
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* 底部首层渠道 */}
      {trace.firstChannel && (
        <div className="text-xs text-gray-500 border-t border-gray-100 pt-2">
          首层渠道：<span className="text-gray-700 font-medium">{trace.firstChannel.channelName}</span>
        </div>
      )}
    </div>
  );
}
